<script>
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import SectionLayout from './SectionLayout.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: {
    SectionLayout,
    NextButton,
  },
  props: {
    smtpConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    return { v$: useVuelidate() };
  },
  data() {
    return {
      isSMTPEnabled: false,
      smtpAddress: '',
      smtpPort: '',
      smtpLogin: '',
      smtpPassword: '',
      smtpDomain: '',
      smtpSSL: false,
      smtpStartTLS: true,
      smtpOpenSSLVerifyMode: 'none',
      smtpAuthentication: 'login',
      isUpdatingSMTP: false,
      encryptionProtocols: [
        { id: 'ssl', title: 'SSL/TLS', checked: false },
        { id: 'starttls', title: 'STARTTLS', checked: true },
      ],
      openSSLVerifyModes: [
        { key: 1, value: 'none' },
        { key: 2, value: 'peer' },
        { key: 3, value: 'client_once' },
        { key: 4, value: 'fail_if_no_peer_cert' },
      ],
      authMechanisms: [
        { key: 1, value: 'plain' },
        { key: 2, value: 'login' },
        { key: 3, value: 'cram-md5' },
        { key: 4, value: 'xoauth' },
        { key: 5, value: 'xoauth2' },
        { key: 6, value: 'ntlm' },
        { key: 7, value: 'gssapi' },
      ],
    };
  },
  validations: {
    smtpAddress: { required },
    smtpPort: { required, minLength: minLength(2) },
    smtpLogin: { required },
    smtpPassword: { required },
    smtpDomain: { required },
  },
  watch: {
    smtpConfig: {
      handler() {
        this.setDefaults();
      },
      deep: true,
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.isSMTPEnabled = this.smtpConfig.smtp_enabled || false;
      this.smtpAddress = this.smtpConfig.smtp_address || '';
      this.smtpPort = this.smtpConfig.smtp_port || '';
      this.smtpLogin = this.smtpConfig.smtp_login || '';
      this.smtpPassword = this.smtpConfig.smtp_password || '';
      this.smtpDomain = this.smtpConfig.smtp_domain || '';
      this.smtpStartTLS =
        this.smtpConfig.smtp_enable_starttls_auto === undefined
          ? true
          : this.smtpConfig.smtp_enable_starttls_auto;
      this.smtpSSL = this.smtpConfig.smtp_enable_ssl_tls || false;
      this.smtpOpenSSLVerifyMode =
        this.smtpConfig.smtp_openssl_verify_mode || 'none';
      this.smtpAuthentication = this.smtpConfig.smtp_authentication || 'login';

      this.encryptionProtocols = [
        { id: 'ssl', title: 'SSL/TLS', checked: this.smtpSSL },
        { id: 'starttls', title: 'STARTTLS', checked: this.smtpStartTLS },
      ];
    },
    handleEncryptionChange(encryption) {
      if (encryption.id === 'ssl') {
        this.smtpSSL = true;
        this.smtpStartTLS = false;
      } else {
        this.smtpSSL = false;
        this.smtpStartTLS = true;
      }
    },
    async updateAccountSMTP() {
      this.v$.$touch();
      if (this.v$.$invalid && this.isSMTPEnabled) {
        useAlert(this.$t('GENERAL_SETTINGS.FORM.ERROR'));
        return;
      }

      this.isUpdatingSMTP = true;
      try {
        await this.$store.dispatch('accounts/update', {
          smtp_enabled: this.isSMTPEnabled,
          smtp_address: this.smtpAddress,
          smtp_port: this.smtpPort,
          smtp_login: this.smtpLogin,
          smtp_password: this.smtpPassword,
          smtp_domain: this.smtpDomain,
          smtp_enable_ssl_tls: this.smtpSSL,
          smtp_enable_starttls_auto: this.smtpStartTLS,
          smtp_openssl_verify_mode: this.smtpOpenSSLVerifyMode,
          smtp_authentication: this.smtpAuthentication,
        });
        useAlert(this.$t('INBOX_MGMT.SMTP.EDIT.SUCCESS_MESSAGE'));
      } catch (error) {
        useAlert(
          error.message || this.$t('INBOX_MGMT.SMTP.EDIT.ERROR_MESSAGE')
        );
      } finally {
        this.isUpdatingSMTP = false;
      }
    },
  },
};
</script>

<template>
  <SectionLayout
    :title="$t('GENERAL_SETTINGS.FORM.ACCOUNT_SMTP.TITLE')"
    :description="$t('GENERAL_SETTINGS.FORM.ACCOUNT_SMTP.SUBTITLE')"
  >
    <form class="grid gap-4" @submit.prevent="updateAccountSMTP">
      <label for="toggle-enable-account-smtp">
        <input
          v-model="isSMTPEnabled"
          type="checkbox"
          name="toggle-enable-account-smtp"
          class="ltr:mr-1 rtl:ml-1"
        />
        {{ $t('GENERAL_SETTINGS.FORM.ACCOUNT_SMTP.TOGGLE_AVAILABILITY') }}
      </label>
      <p>{{ $t('GENERAL_SETTINGS.FORM.ACCOUNT_SMTP.TOGGLE_HELP') }}</p>

      <template v-if="isSMTPEnabled">
        <woot-input
          v-model="smtpAddress"
          :class="{ error: v$.smtpAddress.$error }"
          class="w-full"
          :label="$t('INBOX_MGMT.SMTP.ADDRESS.LABEL')"
          :placeholder="$t('INBOX_MGMT.SMTP.ADDRESS.PLACE_HOLDER')"
          @blur="v$.smtpAddress.$touch"
        />
        <woot-input
          v-model="smtpPort"
          type="number"
          :class="{ error: v$.smtpPort.$error }"
          class="w-full"
          :label="$t('INBOX_MGMT.SMTP.PORT.LABEL')"
          :placeholder="$t('INBOX_MGMT.SMTP.PORT.PLACE_HOLDER')"
          @blur="v$.smtpPort.$touch"
        />
        <woot-input
          v-model="smtpLogin"
          :class="{ error: v$.smtpLogin.$error }"
          class="w-full"
          :label="$t('INBOX_MGMT.SMTP.LOGIN.LABEL')"
          :placeholder="$t('INBOX_MGMT.SMTP.LOGIN.PLACE_HOLDER')"
          @blur="v$.smtpLogin.$touch"
        />
        <woot-input
          v-model="smtpPassword"
          :class="{ error: v$.smtpPassword.$error }"
          class="w-full"
          :label="$t('INBOX_MGMT.SMTP.PASSWORD.LABEL')"
          :placeholder="$t('INBOX_MGMT.SMTP.PASSWORD.PLACE_HOLDER')"
          type="password"
          @blur="v$.smtpPassword.$touch"
        />
        <woot-input
          v-model="smtpDomain"
          :class="{ error: v$.smtpDomain.$error }"
          class="w-full"
          :label="$t('INBOX_MGMT.SMTP.DOMAIN.LABEL')"
          :placeholder="$t('INBOX_MGMT.SMTP.DOMAIN.PLACE_HOLDER')"
          @blur="v$.smtpDomain.$touch"
        />

        <div class="grid gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SMTP.ENCRYPTION') }}
          </label>
          <select
            :value="smtpSSL ? 'ssl' : 'starttls'"
            class="!mb-0 text-sm"
            @change="handleEncryptionChange({ id: $event.target.value })"
          >
            <option
              v-for="protocol in encryptionProtocols"
              :key="protocol.id"
              :value="protocol.id"
            >
              {{ protocol.title }}
            </option>
          </select>
        </div>

        <div class="grid gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SMTP.OPEN_SSL_VERIFY_MODE') }}
          </label>
          <select
            v-model="smtpOpenSSLVerifyMode"
            class="!mb-0 text-sm"
            name="account-smtp-openssl-mode"
          >
            <option
              v-for="mode in openSSLVerifyModes"
              :key="mode.key"
              :value="mode.value"
            >
              {{ mode.value }}
            </option>
          </select>
        </div>

        <div class="grid gap-2">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SMTP.AUTH_MECHANISM') }}
          </label>
          <select
            v-model="smtpAuthentication"
            class="!mb-0 text-sm"
            name="account-smtp-auth-mechanism"
          >
            <option
              v-for="mechanism in authMechanisms"
              :key="mechanism.key"
              :value="mechanism.value"
            >
              {{ mechanism.value }}
            </option>
          </select>
        </div>
      </template>

      <div>
        <NextButton
          :label="$t('GENERAL_SETTINGS.FORM.ACCOUNT_SMTP.UPDATE')"
          :is-loading="isUpdatingSMTP"
          type="submit"
          :disabled="(v$.$invalid && isSMTPEnabled) || isUpdatingSMTP"
        />
      </div>
    </form>
  </SectionLayout>
</template>
